import { Button, DataTable, Form, Modal, ResultsCard, ResultsToolbar, RowActions } from 'shared/components/ui';
import React, { useState, useEffect, useCallback } from 'react';
import { PersonSearch, Add, UnfoldMore, Save } from '@mui/icons-material';

import { Search, Clear } from '@mui/icons-material';
import Header from '../../../shared/components/layout/Header';
import Footer from '../../../shared/components/layout/Footer';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import { Toast } from '../../../shared/components/Toast';
import { derivadoresAPI, localidadesAPI } from '../../../core/api/api';
import { useSortableTable } from '../../../shared/hooks/useSortableTable';
import { textMatch } from '../../../shared/utils/textUtils';
import '../../../features/turnos/components/GrillaTurnos.css';
import '../../../styles/buttons.css';

const PAGE_SIZE = 50;
const EMPTY = { nombre: '', especialidad: '', idLocalidad: '' };

const GrillaDerivadores = () => {
  const [derivadores, setDerivadores] = useState([]);
  const [localidades, setLocalidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [buscar, setBuscar] = useState('');
  const [page, setPage] = useState(1);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const { sortedData, requestSort, getSortIcon } = useSortableTable(derivadores);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, lRes] = await Promise.all([
        derivadoresAPI.getDerivadores(),
        localidadesAPI.getLocalidades(),
      ]);
      setDerivadores(dRes.data || []);
      setLocalidades(lRes.data || []);
    } catch {
      setToast({ message: 'Error al cargar los datos.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const filtrados = sortedData.filter(d =>
    !buscar ||
    textMatch(d.nombre, buscar) ||
    textMatch(d.especialidad, buscar)
  );
  const totalPages = Math.ceil(filtrados.length / PAGE_SIZE);
  const paginados = filtrados.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const esDuplicado = () => {
    const val = form?.nombre?.trim().toUpperCase();
    if (!val) return false;
    if (editando) return false;
    return derivadores.some(d => d.nombre?.toUpperCase() === val);
  };

  const handleNuevo = () => { setEditando(null); setForm(EMPTY); setMostrarForm(true); };
  const handleEditar = (d) => {
    setEditando(d);
    setForm({
      nombre: d.nombre || '',
      especialidad: d.especialidad || '',
      idLocalidad: d.idLocalidad != null ? String(d.idLocalidad) : '',
    });
    setMostrarForm(true);
  };
  const handleCancelar = () => { setMostrarForm(false); setEditando(null); setForm(EMPTY); };

  const handleGuardar = async (e) => {
    e.preventDefault();
    if (esDuplicado()) {
      setToast({ message: 'Ya existe un derivador con ese nombre.', type: 'error' });
      return;
    }
    setLoading(true);
    try {
      if (editando) {
        await derivadoresAPI.actualizar(editando.idDerivador, {
          nombre: form.nombre.trim(),
          especialidad: form.especialidad.trim(),
          idLocalidad: Number(form.idLocalidad),
        });
        setToast({ message: 'Derivador actualizado con éxito.', type: 'success' });
      } else {
        await derivadoresAPI.guardar({
          nombre: form.nombre.trim(),
          especialidad: form.especialidad.trim(),
          idLocalidad: Number(form.idLocalidad),
          usrAlta: '',
        });
        setToast({ message: 'Derivador guardado con éxito.', type: 'success' });
      }
      handleCancelar();
      cargar();
    } catch (err) {
      setToast({ message: err.response?.status === 409 ? 'Ya existe un derivador con ese nombre.' : 'Error al guardar el derivador.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const confirmarEliminar = async () => {
    try {
      await derivadoresAPI.eliminar(confirmEliminar);
      setToast({ message: 'Derivador eliminado con éxito.', type: 'success' });
      cargar();
    } catch {
      setToast({ message: 'Error al eliminar el derivador.', type: 'error' });
    } finally {
      setConfirmEliminar(null);
    }
  };

  const duplicado = mostrarForm && esDuplicado();

  return (
    <div className="grilla-turnos">
      <Header />
      <div className="main-content">
        <div className="page-header">
          <h2 className="page-title-grilla">
            <PersonSearch sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#6d1212' }} />
            Derivadores
          </h2>
          <span className="page-subtitle">Administración de derivadores</span>
        </div>

        <Modal show={mostrarForm} onHide={handleCancelar}>
          <Modal.Header closeButton>
            <Modal.Title>{editando ? 'Editar Derivador' : 'Nuevo Derivador'}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleGuardar}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>* Derivador</Form.Label>
                <Form.Control
                  type="text"
                  maxLength={50}
                  placeholder="Derivador..."
                  value={form.nombre}
                  onChange={(e) => setForm(f => ({ ...f, nombre: e.target.value }))}
                  isInvalid={duplicado}
                  required
                  autoFocus
                />
                {duplicado && <Form.Control.Feedback type="invalid">Ya existe un derivador con ese nombre.</Form.Control.Feedback>}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>* Especialidad</Form.Label>
                <Form.Control
                  type="text"
                  maxLength={50}
                  placeholder="Especialidad..."
                  value={form.especialidad}
                  onChange={(e) => setForm(f => ({ ...f, especialidad: e.target.value }))}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>* Localidad</Form.Label>
                <Form.Select
                  key={form.idLocalidad}
                  value={form.idLocalidad}
                  onChange={(e) => setForm(f => ({ ...f, idLocalidad: e.target.value }))}
                  required
                >
                  <option value="">Seleccione...</option>
                  {localidades.map(l => (
                    <option key={l.idLocalidad} value={String(l.idLocalidad)}>{l.localidad}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCancelar} disabled={loading}>Cancelar</Button>
              <Button type="submit" variant="success" disabled={loading || duplicado || !form.nombre.trim() || !form.especialidad.trim() || !form.idLocalidad}>
                <Save sx={{ fontSize: 16, mr: 0.5 }} /> {editando ? 'Actualizar' : 'Guardar'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <div className="filters-card">
          <div className="filters-header">
            <h3 className="filters-title">Filtros de Búsqueda</h3>
          </div>
          <div className="filters-content">
            <div className="filters-row">
              <div className={`filter-group ${buscar ? 'filter-active' : ''}`}>
                <label className="filter-label">Derivador / Especialidad</label>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Buscar..."
                  value={buscar}
                  onChange={(e) => { setBuscar(e.target.value); setPage(1); }}
                />
              </div>
              <div className="filter-actions">
                <Button className="btn-search" onClick={() => setPage(1)} disabled={loading}>
                  <Search sx={{ fontSize: 18, mr: 0.5 }} /> Buscar
                </Button>
                <Button className="btn-clear" onClick={() => { setBuscar(''); setPage(1); }}>
                  <Clear sx={{ fontSize: 18, mr: 0.5 }} /> Limpiar
                </Button>
              </div>
            </div>
          </div>
        </div>

        <ResultsCard className="results-card">
          <ResultsToolbar className="results-header">
            <div className="results-header-left">
              <h3 className="results-title">Resultados</h3>
            </div>
            <div className="results-info">
              <span className="results-count">{filtrados.length} derivadores encontrados</span>
              {loading && <span className="loading-indicator">Cargando...</span>}
              <Button variant="success" size="sm" onClick={handleNuevo} className="btn-new-turno">
                <Add sx={{ fontSize: 18, mr: 0.5 }} /> Nuevo Derivador
              </Button>
            </div>
          </ResultsToolbar>
          <div className="table-container table-container-scrollable">
            <DataTable className="data-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('idDerivador')} style={{ cursor: 'pointer' }}>
                    # {getSortIcon('idDerivador') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('nombre')} style={{ cursor: 'pointer' }}>
                    Derivador {getSortIcon('nombre') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('especialidad')} style={{ cursor: 'pointer' }}>
                    Especialidad {getSortIcon('especialidad') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('localidad')} style={{ cursor: 'pointer' }}>
                    Localidad {getSortIcon('localidad') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginados.length === 0 ? (
                  <tr><td colSpan="5" className="empty-state">{loading ? 'Cargando...' : 'No hay derivadores para mostrar'}</td></tr>
                ) : (
                  paginados.map((d) => (
                    <tr key={d.idDerivador}>
                      <td>{d.idDerivador}</td>
                      <td>{d.nombre}</td>
                      <td>{d.especialidad}</td>
                      <td>{d.localidad}</td>
                      <td className="action-cell">
                        <RowActions onEdit={() => handleEditar(d)} onDelete={() => setConfirmEliminar(d.idDerivador)} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </DataTable>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', padding: '1rem' }}>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(1)} disabled={page === 1}>«</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>‹</Button>
              <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Página {page} de {totalPages}</span>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>›</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</Button>
            </div>
          )}
        </ResultsCard>
      </div>
      <Footer />

      <ConfirmModal
        show={confirmEliminar !== null}
        title="Eliminar derivador"
        message="¿Estás seguro que querés eliminar este derivador?"
        onConfirm={confirmarEliminar}
        onCancel={() => setConfirmEliminar(null)}
      />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default GrillaDerivadores;
